import React, { useContext, useEffect, useState } from 'react';
import { useLocation, Outlet } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { EVENTS } from 'constants/events';
import { logEvent } from 'utils/amplitude';
import styled from 'styled-components';
import Body2 from '@joinforma/ui/components/Body2';
import Title2 from '@joinforma/ui/components/Title2';
import { ReactComponent as ExclamationMarkIcon } from '@joinforma/ui/assets/icons/exclamation-mark.svg';

import * as EmployeeUtils from 'utils/employee';

import CurrentUserContext from 'contexts/CurrentUserContext';
import PretaxContext from 'contexts/PretaxContext';
import FeatureFlagContext from 'contexts/FeatureFlagContext';

import { FEATURE_FLAGS } from 'constants/featureFlag';
import TopNavigationBar from './TopNavigationBar';
import Footer from './Footer';
import EmailBanner from './EmailBanner';
import InterstitialTaskModal from './InterstitialTaskModal';
import CipDocumentUploadModal from 'components/HsaAccountSetup/CipDocumentUploadModal';
import SetupDirectDepositBanner from './SetupDirectDepositBanner';
import CipDocumentUploadBanner from 'components/HsaAccountSetup/CipDocumentUploadBanner';
import useQueryPretaxEmployeeDetails from 'hooks/useQueryPretaxEmployeeDetails';
import useQueryConnectedAccounts from 'hooks/useQueryConnectedAccounts';
import useQueryCipApplication from 'hooks/useQueryCipApplication';
import useSnackbar from '@joinforma/ui/hooks/useSnackbar';

import { Employee, Company } from './types';
import { CONNECTED_ACCOUNT_STATUSES } from 'constants/stripe';
import { CIP_APPLICATION_STATUSES } from 'constants/cip_application';

const Content = styled.main`
  background-color: ${({
    theme: {
      colors: {
        background: { primary },
      },
    },
  }) => primary};
  min-height: 100vh;
`;

const AccessAlert = styled(Body2)`
  display: block;
  height: 48px;
  background: #fff9cc;
  color: ${({
    theme: {
      colors: {
        text: { primary },
      },
    },
  }) => primary};
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  top: 0;
  z-index: 1000;
  width: 100%;
  svg {
    height: 14.86px;
    width: 14.86px;
  }
`;

const AlertDescription = styled(Body2)`
  display: block;
`;

const AccessType = styled(Title2)`
  margin-left: 13px;
  margin-right: 4px;
`;

interface RootProps {
  paddingTop: string;
}

const Root = styled.div<RootProps>`
  position: relative;
  padding-top: ${({ paddingTop = '0px' }) => paddingTop};
`;

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!);

const checkIfMissingStripeDirectDeposit = (args: {
  is_unit_enabled: boolean;
  employee: {
    settings: {
      country: string;
      external_payment: { connected_account?: { status: string } };
    };
  };
  company: { features: { direct_deposit: boolean } };
  has_pretax_accounts: boolean;
  pretax_employee_details?: { bank?: { account_number: string } };
}): boolean => {
  const {
    is_unit_enabled,
    employee,
    company,
    has_pretax_accounts,
    pretax_employee_details,
  } = args;
  const {
    settings: {
      country,
      external_payment: { connected_account },
    },
  } = employee;
  const {
    features: { direct_deposit: posttax_direct_deposit_enabled },
  } = company;
  const is_usa = country === 'us';

  const result =
    !is_unit_enabled &&
    is_usa &&
    ((posttax_direct_deposit_enabled &&
      (!connected_account || connected_account.status !== 'active')) ||
      (pretax_employee_details &&
        has_pretax_accounts &&
        (((!pretax_employee_details.bank ||
          !pretax_employee_details.bank.account_number) &&
          connected_account &&
          connected_account.status === 'active') ||
          !connected_account)));
  return !!result;
};

const checkIfMissingUnitDirectDeposit = ({
  is_unit_enabled,
  connected_account_status,
}: {
  is_unit_enabled: boolean;
  connected_account_status?: string;
}) =>
  is_unit_enabled &&
  connected_account_status !== CONNECTED_ACCOUNT_STATUSES.ACTIVE;

const isPrivacyPolicyVisible = (company_id: string) =>
  ['microsoft'].includes(company_id);

const BaseLayout = ({ children }: { children?: React.ReactNode }) => {
  const feature_flag = useContext(FeatureFlagContext);
  const new_home_page_enabled: boolean = feature_flag?.flag(
    FEATURE_FLAGS.CLIENT_HOME_FEED_PAGE,
    false
  );
  const unit_or_stripe: string = feature_flag.flag(
    FEATURE_FLAGS.UNIT_OR_STRIPE,
    false
  );
  const is_unit_enabled = unit_or_stripe === 'unit';

  const CIP_MODAL_DISMISSED_KEY = 'cip-application-banner-dismissed';

  const { showErrorSnackbar } = useSnackbar();

  const location = useLocation();
  const { onboarding } = (location.state as { onboarding?: boolean }) ?? {
    onboarding: false,
  };

  const { employee, company }: { employee: Employee; company: Company } =
    useContext(CurrentUserContext);
  const { alegeus_pretax_accounts: pretax_accounts, new_pretax_experience } =
    useContext(PretaxContext);
  const { id: company_id } = company;
  const {
    show_signin_banner,

    settings: {
      is_cdh_enabled,
      external_payment: { connected_account },
    },
  } = employee;
  const {
    data: pretax_employee_details_data,
    is_loading: is_pretax_employee_details_loading,
  } = useQueryPretaxEmployeeDetails({
    is_cdh_enabled: is_cdh_enabled && !new_pretax_experience,
  });

  const { data: connected_accounts_data } = useQueryConnectedAccounts({
    status: [
      CONNECTED_ACCOUNT_STATUSES.ACTIVE,
      CONNECTED_ACCOUNT_STATUSES.PENDING_VERIFICATION,
    ],
    enabled: is_unit_enabled,
    onError: () => {
      showErrorSnackbar({
        text: 'An error occurred while fetching your bank account.',
      });
    },
  });

  const { data: cip_application_data } = useQueryCipApplication({
    enabled: false,
    onError: () => {
      showErrorSnackbar({
        text: 'An error occurred while fetching your bank account.',
      });
    },
  });

  const unit_connected_account_status =
    connected_accounts_data?.connected_accounts?.[0]?.status;

  const stripe_connected_account_status = connected_account?.status;

  const show_direct_deposit_alert_box = is_pretax_employee_details_loading
    ? false
    : checkIfMissingStripeDirectDeposit({
        is_unit_enabled,
        employee,
        company,
        has_pretax_accounts: pretax_accounts.length > 0,
        pretax_employee_details:
          pretax_employee_details_data?.pretax_employee_details || {},
      }) ||
      checkIfMissingUnitDirectDeposit({
        is_unit_enabled,
        connected_account_status: unit_connected_account_status,
      });

  const [is_onboarding, setIsOnboarding] = useState(
    location.state ? onboarding : false
  );

  const [is_cip_application_awaiting_documents, setCipDocumentUploadModal] =
    useState(true);

  const [cip_modal_dismissed, set_cip_modal_dismissed] = useState<boolean>(
    localStorage.getItem(CIP_MODAL_DISMISSED_KEY) === 'true'
  );

  useEffect(() => {
    if (is_onboarding) {
      logEvent(EVENTS.OnboardingInterstitialTasksViewed, {});
    }
  }, [is_onboarding]);

  const is_usa = EmployeeUtils.getCountry({ employee }) === 'us';
  const has_pretax_accounts = EmployeeUtils.hasPretaxAccounts({
    employee,
    pretax_accounts,
  });
  const has_personal_email = employee && employee.personal_email;

  const onOnboardingModalClose = () => {
    setIsOnboarding(false);
    logEvent(EVENTS.OnboardingInterstitialTasksDismissed, {});
    window.history.replaceState({}, document.title);
  };

  const onCipDocumentUploadModalClose = () => {
    setCipDocumentUploadModal(false);
    localStorage.setItem(CIP_MODAL_DISMISSED_KEY, 'true');
    set_cip_modal_dismissed(true);
  };

  return (
    <Elements stripe={stripePromise}>
      <Root paddingTop={show_signin_banner ? '48px' : '0'}>
        {show_signin_banner && (
          <AccessAlert>
            <ExclamationMarkIcon />
            <AccessType>Read Only Access Enabled: </AccessType>
            <AlertDescription>
              You are currently logged in as a member via the Forma Internal
              Portal
            </AlertDescription>
          </AccessAlert>
        )}
        <TopNavigationBar
          employee={employee}
          company={company}
          new_home_page_enabled={new_home_page_enabled}
          current_path={location.pathname}
        />
        {!has_personal_email && is_usa && has_pretax_accounts && (
          <EmailBanner first_name={employee.first_name} />
        )}
        {show_direct_deposit_alert_box && (
          <SetupDirectDepositBanner
            connected_account_status={
              unit_connected_account_status || stripe_connected_account_status
            }
          />
        )}
        {cip_application_data?.bankApplicationStatus ===
          CIP_APPLICATION_STATUSES.AWAITING_DOCUMENTS && (
          <CipDocumentUploadBanner
            bankApplicationFormUrl={
              cip_application_data?.bankApplicationFormUrl
            }
          />
        )}
        <Content>
          {children ?? <Outlet />}
          {is_onboarding && (
            <InterstitialTaskModal
              employee={employee}
              company={company}
              onClose={onOnboardingModalClose}
              visible={is_onboarding}
            />
          )}
        </Content>
        {cip_application_data?.bankApplicationStatus ===
          CIP_APPLICATION_STATUSES.AWAITING_DOCUMENTS &&
          !cip_modal_dismissed && (
            <CipDocumentUploadModal
              onCancel={onCipDocumentUploadModalClose}
              visible={
                !cip_modal_dismissed && is_cip_application_awaiting_documents
              }
              bankApplicationFormUrl={
                cip_application_data?.bankApplicationFormUrl
              }
            />
          )}
        <Footer showCompanyPrivacyPolicy={isPrivacyPolicyVisible(company_id)} />
      </Root>
    </Elements>
  );
};

export default BaseLayout;
